import { PageController } from '@client/components/_core/layout/PageController';
import { VehicleTab } from '@client/components/claims/vehicle/VehicleTab';
import { DETAIL_RECORD_PREFETCH_OPTIONS } from '@client/trpc/query-options';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { runDetailRoutePrefetchWhenAuthorised } from '../../../detail-route-prefetch';
import { isClaimDetailPrefetchAuthorised } from '../claim-record-query';
import { claimFormStore } from '../store';

/**
 * Replaces the former separate vehicle-inspection and vehicle-assessment tabs. Deliberately
 * carries no `featureFlag` in staticData: the tab is gated on the claim's product-type
 * category, and only the AI-scan *section* inside is flag-gated — a route-level flag would
 * also hide the (unflagged) repair assessment.
 */
export const Route = createFileRoute('/console/$orgId/claims/details/$id/vehicle')({
    staticData: {
        metadata: {
            title: 'Vehicle',
        },
    },
    beforeLoad: ({ context, params, preload, abortController }): void => {
        // Remove the claim-to-vehicle waterfall during route loading. The claim record itself is
        // warmed by the parent details route. The warm-up waits for an authorised session, as
        // the parent's does: deferred on a cold deep link, never deferred for hover preloads and
        // torn down when the match aborts. Fire-and-forget; a failed warm-up stays silent and
        // falls through to the render-time queries and their existing error handling.
        runDetailRoutePrefetchWhenAuthorised(
            () => isClaimDetailPrefetchAuthorised(params.orgId),
            () => {
                void context.trpcUtils.vehicleInspection.listForClaim.prefetch(
                    { claimId: params.id },
                    { retry: 1, staleTime: 60_000, meta: DETAIL_RECORD_PREFETCH_OPTIONS.meta },
                );
                void context.trpcUtils.vehicleAssessment.getForClaim.prefetch(
                    { claimId: params.id },
                    { retry: 1, staleTime: 30_000, meta: DETAIL_RECORD_PREFETCH_OPTIONS.meta },
                );
            },
            { defer: !preload, signal: abortController.signal },
        );
    },
    component: VehicleRoute,
});

function VehicleRoute() {
    const claim = claimFormStore.use.formData();

    // The Outlet hosts the full report overlay (`report/$inspectionId`) above the tab.
    return (
        <PageController route={Route}>
            {claim && <VehicleTab claimId={BigInt(claim.id)} />}
            <Outlet />
        </PageController>
    );
}
